/**
 * ANALYTICS SERVICE v1.1
 * ✅ fix 1: setUserProperties usa gtag('config') para user_id (GA4 correcto)
 * ✅ fix 2: inviteCode hasheado antes de mandarlo a GA4
 * ✅ fix 3: substr → substring (no deprecado)
 * ✅ nuevo: trackRetentionPing para D1/D7 retention (beta)
 */

// ============================================
// EVENTOS CORE
// ============================================
const EVENTS = {
  // Onboarding
  ONBOARDING_STARTED:        'onboarding_started',
  ONBOARDING_STEP_COMPLETED: 'onboarding_step_completed',
  ONBOARDING_COMPLETED:      'onboarding_completed',
  ONBOARDING_SKIPPED:        'onboarding_skipped',

  // Activación
  FIRST_COMPONENT_CREATED: 'first_component_created',
  FIRST_RECIPE_CREATED:    'first_recipe_created',
  FIRST_PLAN_CREATED:      'first_plan_created',
  EXAMPLES_LOADED:         'examples_loaded',

  // Engagement
  COMPONENT_CREATED:       'component_created',
  RECIPE_CREATED:          'recipe_created',
  MEAL_PLANNED:            'meal_planned',
  SHOPPING_LIST_CREATED:   'shopping_list_created',
  RECIPE_SHARED:           'recipe_shared',

  // Features
  STOCK_ITEM_ADDED:    'stock_item_added',
  CHAT_MESSAGE_SENT:   'chat_message_sent',
  TASK_CREATED:        'task_created',
  STATS_VIEWED:        'stats_viewed',

  // Errores
  ERROR_OCCURRED: 'error_occurred',

  // Conversión
  INVITE_SENT:      'invite_sent',
  HOUSEHOLD_JOINED: 'household_joined',

  // Retention (beta)
  RETENTION_PING: 'retention_ping',
};

// ============================================
// HELPERS DE MÓDULO
// ============================================

// fix 2: hash simple del inviteCode — no mandamos el código real a GA4
async function hashValue(value) {
  try {
    const encoder = new TextEncoder();
    const data = encoder.encode(String(value));
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('').slice(0, 12);
  } catch {
    return 'hashed';
  }
}

// ============================================
// SERVICIO DE ANALYTICS
// ============================================
class AnalyticsService {
  constructor() {
    this.isEnabled = false;
    this.sessionStart = Date.now();
  }

  // ─── Inicializar ──────────────────────────────────────────────────────────
  init() {
    try {
      if (window.gtag) {
        this.isEnabled = true;
        console.log('✅ Analytics inicializado');
        this.trackEvent('session_start', {
          session_id: this.getSessionId(),
        });
      } else {
        console.warn('⚠️ Google Analytics no disponible');
      }
    } catch (error) {
      console.error('❌ Error inicializando Analytics:', error);
    }
  }

  // ─── Track evento genérico ────────────────────────────────────────────────
  trackEvent(eventName, params = {}) {
    if (!this.isEnabled) return;
    try {
      const eventData = {
        ...params,
        timestamp: new Date().toISOString(),
        session_duration: Math.floor((Date.now() - this.sessionStart) / 1000),
      };
      if (window.gtag) {
        window.gtag('event', eventName, eventData);
      }
      if (import.meta.env.DEV) {
        console.log('📊 Event:', eventName, eventData);
      }
    } catch (error) {
      console.error('❌ Error tracking event:', error);
    }
  }

  // ─── Track page view ──────────────────────────────────────────────────────
  trackPageView(pageName, path) {
    this.trackEvent('page_view', {
      page_title: pageName,
      page_path: path,
    });
  }

  // ─── Track error ──────────────────────────────────────────────────────────
  trackError(errorMessage, errorContext = {}) {
    this.trackEvent(EVENTS.ERROR_OCCURRED, {
      error_message: errorMessage,
      error_context: JSON.stringify(errorContext),
    });
  }

  // ============================================
  // ONBOARDING EVENTS
  // ============================================
  trackOnboardingStarted() {
    this.trackEvent(EVENTS.ONBOARDING_STARTED);
  }

  trackOnboardingStepCompleted(stepNumber, stepId) {
    this.trackEvent(EVENTS.ONBOARDING_STEP_COMPLETED, {
      step_number: stepNumber,
      step_id: stepId,
    });
  }

  trackOnboardingCompleted(totalTime) {
    this.trackEvent(EVENTS.ONBOARDING_COMPLETED, {
      completion_time: totalTime,
    });
  }

  trackOnboardingSkipped(stepNumber) {
    this.trackEvent(EVENTS.ONBOARDING_SKIPPED, {
      skipped_at_step: stepNumber,
    });
  }

  // ============================================
  // ACTIVACIÓN EVENTS
  // ============================================
  trackFirstComponentCreated(timeSinceSignup) {
    this.trackEvent(EVENTS.FIRST_COMPONENT_CREATED, {
      time_to_first_component: timeSinceSignup,
    });
  }

  trackFirstRecipeCreated(timeSinceSignup) {
    this.trackEvent(EVENTS.FIRST_RECIPE_CREATED, {
      time_to_first_recipe: timeSinceSignup,
    });
  }

  trackFirstPlanCreated(timeSinceSignup) {
    this.trackEvent(EVENTS.FIRST_PLAN_CREATED, {
      time_to_first_plan: timeSinceSignup,
    });
  }

  trackExamplesLoaded(source, count) {
    this.trackEvent(EVENTS.EXAMPLES_LOADED, {
      source,           // 'components' | 'recipes'
      examples_count: count,
    });
  }

  // ============================================
  // ENGAGEMENT EVENTS
  // ============================================
  trackComponentCreated(componentType, isFromExample) {
    this.trackEvent(EVENTS.COMPONENT_CREATED, {
      component_type: componentType,
      from_example: isFromExample,
    });
  }

  trackRecipeCreated(recipeCategory, componentsCount) {
    this.trackEvent(EVENTS.RECIPE_CREATED, {
      recipe_category: recipeCategory,
      components_count: componentsCount,
    });
  }

  trackMealPlanned(mealType, planMethod) {
    this.trackEvent(EVENTS.MEAL_PLANNED, {
      meal_type: mealType,     // 'breakfast' | 'lunch' | 'snack' | 'dinner'
      plan_method: planMethod, // 'recipe' | 'combo' | 'manual'
    });
  }

  trackShoppingListCreated(itemsCount, source) {
    this.trackEvent(EVENTS.SHOPPING_LIST_CREATED, {
      items_count: itemsCount,
      source, // 'manual' | 'from_plan'
    });
  }

  trackRecipeShared(shareMethod) {
    this.trackEvent(EVENTS.RECIPE_SHARED, {
      share_method: shareMethod, // 'whatsapp' | 'telegram' | 'link' | 'qr' | 'pdf'
    });
  }

  // ============================================
  // FEATURE USAGE
  // ============================================
  trackStockItemAdded(category) {
    this.trackEvent(EVENTS.STOCK_ITEM_ADDED, { category });
  }

  trackChatMessageSent() {
    this.trackEvent(EVENTS.CHAT_MESSAGE_SENT);
  }

  trackTaskCreated() {
    this.trackEvent(EVENTS.TASK_CREATED);
  }

  trackStatsViewed() {
    this.trackEvent(EVENTS.STATS_VIEWED);
  }

  // ============================================
  // CONVERSIÓN
  // ============================================
  trackInviteSent() {
    this.trackEvent(EVENTS.INVITE_SENT);
  }

  // fix 2: inviteCode hasheado — no mandamos el código real a GA4
  async trackHouseholdJoined(inviteCode) {
    const hashedCode = await hashValue(inviteCode);
    this.trackEvent(EVENTS.HOUSEHOLD_JOINED, {
      invite_code_hash: hashedCode,
    });
  }

  // ============================================
  // RETENTION (beta)
  // ============================================
  /**
   * Llamar una vez por sesión al montar la app.
   * GA4 calcula D1/D7/D30 automáticamente con 'retention_ping'
   * + el campo days_since_signup que mandamos nosotros.
   *
   * Uso: analyticsService.trackRetentionPing(user.metadata.creationTime)
   */
  trackRetentionPing(signupDateISO) {
    if (!signupDateISO) return;
    const signupDate = new Date(signupDateISO);
    const now = new Date();
    const daysSinceSignup = Math.floor((now - signupDate) / (1000 * 60 * 60 * 24));

    this.trackEvent(EVENTS.RETENTION_PING, {
      days_since_signup: daysSinceSignup,
      // Bucketing manual para filtrar fácil en GA4
      retention_bucket:
        daysSinceSignup === 0 ? 'D0' :
        daysSinceSignup === 1 ? 'D1' :
        daysSinceSignup <= 3  ? 'D2-D3' :
        daysSinceSignup <= 7  ? 'D4-D7' :
        daysSinceSignup <= 14 ? 'D8-D14' :
        daysSinceSignup <= 30 ? 'D15-D30' : 'D30+',
    });
  }

  // ============================================
  // HELPERS
  // ============================================
  getSessionId() {
    let sessionId = sessionStorage.getItem('mealcycle-session-id');
    if (!sessionId) {
      // fix 3: substring en vez de substr (deprecado)
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
      sessionStorage.setItem('mealcycle-session-id', sessionId);
    }
    return sessionId;
  }

  /**
   * fix 1: GA4 requiere user_id en gtag('config') — no en set/user_properties
   * Llamar desde AuthContext cuando el usuario se loguea.
   */
  setUserProperties(userId, properties = {}) {
    if (!this.isEnabled || !window.gtag) return;
    try {
      // user_id va en config, no en set user_properties
      window.gtag('config', import.meta.env.VITE_GA_MEASUREMENT_ID, {
        user_id: userId,
      });
      // El resto de propiedades sí van en user_properties
      if (Object.keys(properties).length > 0) {
        window.gtag('set', 'user_properties', properties);
      }
    } catch (error) {
      console.error('❌ Error setting user properties:', error);
    }
  }

  /**
   * Llamar desde AuthContext en logout.
   */
  clearUserData() {
    if (!this.isEnabled || !window.gtag) return;
    try {
      window.gtag('config', import.meta.env.VITE_GA_MEASUREMENT_ID, {
        user_id: undefined,
      });
      sessionStorage.removeItem('mealcycle-session-id');
    } catch (error) {
      console.error('❌ Error clearing user data:', error);
    }
  }
}

// Singleton
const analyticsService = new AnalyticsService();
export default analyticsService;
export { EVENTS };